import { GiftIcon, PassIcon } from "@xola/icons";
import { Badge, Currency } from "@xola/ui-kit";
import clsx from "clsx";
import React from "react";
import { useHistory } from "react-router-dom";
import { Audit, LineItem, LineItemObject, Purchase, PurchaseItem, RedemptionAction } from "types";
import { useSeller } from "../../../../hooks/useAccount";
import { TimelineItem, TimelinePill } from "../TimelineItem";
import { ProductImage } from "@/components/Common/ProductImage";
import {
    PurchaseDetails,
    PurchaseOrganizer,
} from "@/components/Customers/Contacts/Timeline/Items/PurchaseTimelineItem";
import { PurchaseProductImage } from "@/components/PurchasesMenu/PurchaseProductImage";
import { useResource } from "@/hooks/useResource";

interface CodeRedeemLineItemProps {
    change: LineItem;
}

export const CodeRedeemLineItem = ({ change: lineItem }: CodeRedeemLineItemProps) => {
    const seller = useSeller();
    const title = getRedemptionAction(lineItem) === RedemptionAction.REDEEM ? "Redeemed" : "Removed";

    const code = lineItem.redeemedCode?.code;

    return (
        <TimelineItem title={title} icon={<GiftIcon />} color="primary" audit={lineItem}>
            <div className="flex flex-col space-y-2">
                <div className="flex flex-row space-x-2">
                    <Currency currency={seller.currency} shouldRemoveTrailingZeroes={false}>
                        {Math.abs(lineItem.amount)}
                    </Currency>
                    <Badge color="secondary" icon={<PassIcon />} className="no-translate uppercase">
                        {code}
                    </Badge>
                </div>
                {lineItem?.redeemedFor ? <RedeemedForTimelinePill redeemedFor={lineItem.redeemedFor} /> : null}
            </div>
        </TimelineItem>
    );
};

interface RedeemedForTimelinePillProps {
    redeemedFor: { id: ID; object: string };
}

const RedeemedForTimelinePill = ({ redeemedFor }: RedeemedForTimelinePillProps) => {
    const resourceUrl = redeemedFor.object === "purchase" ? "purchases" : "purchaseItems";
    const { data, isLoading } = useResource<Purchase | PurchaseItem>([resourceUrl, redeemedFor.id], null, {
        apiConfig: {
            suppressErrorToast: [404],
        },
    });

    if (isLoading || !data) {
        return null;
    }

    return resourceUrl === "purchases" ? (
        <PurchaseTimelinePill purchase={data as Purchase} />
    ) : (
        <PurchaseItemTimelinePill purchaseItem={data as PurchaseItem} />
    );
};

const PurchaseItemTimelinePill = ({ purchaseItem }: { purchaseItem: PurchaseItem }) => {
    const history = useHistory();

    const handleClick = () => {
        history.push(`/purchases/${purchaseItem.purchase.id}/${purchaseItem.id}`);
    };

    return (
        <div className={clsx("flex items-center", "cursor-pointer")} onClick={handleClick}>
            <TimelinePill className="relative">
                <div className="flex items-center space-x-4 p-2">
                    <div className="flex min-w-72 items-center" data-testid="code-redeemed-timeline-item">
                        <div className="relative flex">
                            <ProductImage product={purchaseItem.product} />
                        </div>
                        <PurchaseDetails purchase={{ items: [purchaseItem] }} />
                    </div>
                </div>
            </TimelinePill>
        </div>
    );
};

interface PurchaseTimelinePillProps {
    purchase: Purchase;
}

const PurchaseTimelinePill = ({ purchase }: PurchaseTimelinePillProps) => {
    const isMist = purchase.items.length > 1;
    const history = useHistory();

    const handleClick = () => {
        history.push(isMist ? `/purchases/${purchase.id}/` : `/purchases/${purchase.id}/${purchase.items[0]?.id}`);
    };

    return (
        <div className={clsx("flex items-center", "cursor-pointer")} onClick={handleClick}>
            <TimelinePill className="relative">
                {isMist && <PurchaseOrganizer purchase={purchase} />}

                <div className="flex items-center space-x-4 p-2">
                    <div className="flex min-w-72 items-center">
                        <div className="relative flex">
                            <PurchaseProductImage purchase={purchase} />
                        </div>
                        <PurchaseDetails purchase={purchase} />
                    </div>
                </div>
            </TimelinePill>
        </div>
    );
};

CodeRedeemLineItem.supportsAudit = (
    _purchase: Purchase,
    _item: PurchaseItem,
    _audit: Audit,
    _lineItem: PurchaseItem,
    _timezoneName: string,
    change: LineItem,
) => {
    return change.object === LineItemObject.CODE_REDEEM;
};

const getRedemptionAction = (lineItem: LineItem): RedemptionAction => {
    if (lineItem.action) {
        return lineItem.action;
    }

    return lineItem.amount < 0 ? RedemptionAction.REDEEM : RedemptionAction.REMOVE;
};
